import React, { useState, useRef, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight, FaDownload, FaTimes, FaExpand, FaCompress, FaEye, FaBookOpen, FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './PDFViewer.css';

const PDFViewer = () => {
  const [searchParams] = useSearchParams();
  const pdfUrl = searchParams.get('url') || '/3almnyBook.pdf';
  const fileName = searchParams.get('name') || 'كتاب علمني';
  const fromPage = searchParams.get('from') || 'library';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(10);
  const [scale, setScale] = useState(1.0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState('');
  const [showCover, setShowCover] = useState(true);
  const [bookPosition, setBookPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const bookRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // إضافة event listener للـ fullscreen
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    // محاكاة عدد الصفحات (يمكن تعديلها حسب الحاجة)
    setTotalPages(10);
        setLoading(false);
        
        // إخفاء الغلاف عند بدء عرض الصفحات
        if (currentPage > 0) {
          setShowCover(false);
        }
    
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [currentPage]);

  // Add document event listeners for better dragging experience
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (isDragging && scale > 1.0) {
        setBookPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        });
      }
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    if (scale > 1.0) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, scale, dragStart]);

  // Add keyboard event listeners for fullscreen navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only work in fullscreen mode
      if (!isFullscreen) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          if (!isFlipping) goToPrevPage();
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (!isFlipping) goToNextPage();
          break;
        case 'Home':
          e.preventDefault();
          if (!isFlipping) {
            setCurrentPage(1);
            setShowCover(true);
          }
          break;
        case 'End':
          e.preventDefault();
          if (!isFlipping) {
            setCurrentPage(totalPages);
            setShowCover(false);
          }
          break;
        case 'Escape':
          e.preventDefault();
          if (document.fullscreenElement) {
            document.exitFullscreen();
          }
          break;
        default:
          break;
      }
    };

    // Add keyboard listener when in fullscreen
    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen, currentPage, totalPages, isFlipping]);

  // Mouse events for dragging
  const handleMouseDown = (e) => {
    if (scale > 1.0) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - bookPosition.x,
        y: e.clientY - bookPosition.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1.0) {
      setBookPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch events for mobile
  const handleTouchStart = (e) => {
    if (scale > 1.0 && e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - bookPosition.x,
        y: e.touches[0].clientY - bookPosition.y
      });
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && scale > 1.0 && e.touches.length === 1) {
      e.preventDefault();
      setBookPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Reset position when scale changes
  useEffect(() => {
    if (scale <= 1.0) {
      setBookPosition({ x: 0, y: 0 });
    }
  }, [scale]);

  const goToPrevPage = () => {
    if (currentPage > 1 && !isFlipping) {
      setIsFlipping(true);
      setFlipDirection('left');
      // تقليل وقت التأخير من 300ms إلى 200ms
      setTimeout(() => {
        setCurrentPage(prev => prev - 1);
        setShowCover(false);
        setIsFlipping(false);
        setFlipDirection('');
      }, 200);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages && !isFlipping) {
      setIsFlipping(true);
      setFlipDirection('right');
      // تقليل وقت التأخير من 300ms إلى 200ms
      setTimeout(() => {
        setCurrentPage(prev => prev + 1);
        setShowCover(false);
        setIsFlipping(false);
        setFlipDirection('');
      }, 200);
    }
  };

  const goToPage = (page) => {
    const pageNum = parseInt(page);
    if (pageNum >= 0 && pageNum <= totalPages && !isFlipping) {
      const direction = pageNum > currentPage ? 'right' : 'left';
      setIsFlipping(true);
      setFlipDirection(direction);
      // تقليل وقت التأخير من 300ms إلى 200ms
      setTimeout(() => {
        setCurrentPage(pageNum);
        setShowCover(pageNum === 0);
        setIsFlipping(false);
        setFlipDirection('');
      }, 200);
    }
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  const resetZoom = () => {
    setScale(1.0);
  };

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      try {
        await containerRef.current.requestFullscreen();
      } catch (err) {
        console.error('Error attempting to enable fullscreen:', err);
      }
    } else {
      try {
        await document.exitFullscreen();
      } catch (err) {
        console.error('Error attempting to exit fullscreen:', err);
      }
    }
  };

  const downloadPDF = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = fileName || 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openInNewTab = () => {
      window.open(pdfUrl, '_blank');
  };

  const goBack = () => {
    // Return to the page user came from
    if (fromPage === 'files') {
      navigate('/files');
    } else {
      navigate('/library');
    }
  };

  if (error) {
    return (
      <div className="pdf-viewer-page">
        <div className="pdf-error">
          <h3>❌ خطأ في تحميل الملف</h3>
          <p>{error}</p>
          <button onClick={goBack} className="pdf-back-btn">العودة</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pdf-viewer-page" style={{padding : "140px 40px"}}>
      {/* Header */}
      <div className="pdf-viewer-header">
        <div className="pdf-viewer-title">
          <button onClick={goBack} className="pdf-back-button">
            <FaArrowLeft />
          </button>
          <span className="pdf-icon">📚</span>
          <span className="pdf-name">{fileName || 'مستند PDF'}</span>
        </div>
        <div className="pdf-viewer-controls">
          <button onClick={openInNewTab} className="pdf-control-btn pdf-view-btn" title="عرض في نافذة جديدة">
            <FaEye />
          </button>
          <button onClick={downloadPDF} className="pdf-control-btn pdf-download-btn" title="تحميل">
            <FaDownload />
          </button>
          <button onClick={toggleFullscreen} className="pdf-control-btn pdf-fullscreen-btn" title="ملء الشاشة">
            {isFullscreen ? <FaCompress /> : <FaExpand />}
          </button>
        </div>
      </div>

      {/* Fullscreen Instructions */}
      {isFullscreen && (
        <div className="pdf-fullscreen-instructions">
          <div className="pdf-instructions-content">
            <h4>🎮 كيفية التحكم</h4>
            <div className="pdf-keyboard-shortcuts">
              <div className="pdf-shortcut-item">
                <span className="pdf-key">←</span>
                <span className="pdf-description">الصفحة السابقة</span>
              </div>
              <div className="pdf-shortcut-item">
                <span className="pdf-key">→</span>
                <span className="pdf-description">الصفحة التالية</span>
              </div>
              <div className="pdf-shortcut-item">
                <span className="pdf-key">Home</span>
                <span className="pdf-description">العودة للغلاف</span>
              </div>
              <div className="pdf-shortcut-item">
                <span className="pdf-key">End</span>
                <span className="pdf-description">آخر صفحة</span>
              </div>
              <div className="pdf-shortcut-item">
                <span className="pdf-key">ESC</span>
                <span className="pdf-description">خروج من ملء الشاشة</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3D Book Content */}
      <div className="pdf-content" ref={containerRef}>
        {loading && (
          <div className="pdf-loading">
            <div className="pdf-loading-spinner"></div>
            <p>جاري تحميل المستند...</p>
          </div>
        )}
        
        <div className="pdf-book-container" ref={bookRef}>
          <div 
            className={`pdf-book ${isFlipping ? `flipping ${flipDirection}` : ''}`}
            style={{ 
              transform: `translate(${bookPosition.x}px, ${bookPosition.y}px) scale(${scale})`,
              cursor: scale > 1.0 ? (isDragging ? 'grabbing' : 'grab') : 'default'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Book Cover */}
            {showCover && (
              <div className="pdf-book-cover">
                <div className="pdf-cover-content">
                  <FaBookOpen className="pdf-cover-icon" />
                  <h2>{fileName || 'مستند PDF'}</h2>
                  <p>مكتبة علمني</p>
                  <div className="pdf-cover-decoration"></div>
                </div>
              </div>
            )}

            {/* Book Pages */}
            {Array.from({ length: totalPages + 1 }, (_, index) => (
              <div
                key={`page_${index}`}
                className={`pdf-book-page ${currentPage === index ? 'active' : ''}`}
                style={{
                  '--page-number': index,
                  '--total-pages': totalPages + 1,
                  '--current-page': currentPage
                }}
              >
                <div className="pdf-page-content">
                  <div className="pdf-page-header">
                    <span className="pdf-page-number">{index}</span>
                    <span className="pdf-page-title">
                      {index === 0 ? 'الغلاف' : `الصفحة ${index}`}
                    </span>
                  </div>
                  <div className="pdf-page-body">
                    {index === 0 ? (
                      /* الغلاف */
                      <div className="pdf-cover-content">
                        <FaBookOpen className="pdf-cover-icon" />
                        <h2>{fileName || 'مستند PDF'}</h2>
                        <p>مكتبة علمني</p>
                        <div className="pdf-cover-decoration"></div>
                      </div>
                    ) : (
                      /* عرض الكتاب PDF باستخدام Google Viewer */
                      <iframe
                        src={`https://docs.google.com/viewer?url=${encodeURIComponent(pdfUrl)}&embedded=true#page=${index}`}
                        title={`صفحة ${index}`}
                        className="pdf-iframe"
                        frameBorder="0"
                        width="100%"
                        height="100%"
                        allowFullScreen
                      />
                    )}
                  </div>
                  <div className="pdf-page-footer">
                    <span className="book-title">{fileName}</span>
                    <span className="page-info">
                      {index === 0 ? 'الغلاف' : `صفحة ${index} من ${totalPages}`}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {/* Book Back Cover */}
            <div className="pdf-book-back-cover">
              <div className="pdf-back-content">
                <h3>نهاية المستند</h3>
                <p>شكراً لاستخدام مكتبة علمني</p>
                <div className="pdf-back-decoration"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Controls - Now Below the Book */}
      <div className="pdf-navigation">
        <div className="pdf-page-controls">
          <button 
            onClick={goToPrevPage} 
            disabled={currentPage <= 0 || isFlipping}
            className="pdf-nav-btn prev-btn"
          >
            <FaChevronRight />
          </button>
          
          <div className="pdf-page-info">
            <span className="pdf-current-page">{currentPage}</span>
            <span className="pdf-page-separator">من</span>
            <span className="pdf-total-pages">{totalPages}</span>
          </div>
          
          <button 
            onClick={goToNextPage} 
            disabled={currentPage >= totalPages || isFlipping}
            className="pdf-nav-btn next-btn"
          >
            <FaChevronLeft />
          </button>
        </div>

        <div className="pdf-cover-controls">
          <button 
            onClick={() => {
              setCurrentPage(0);
              setShowCover(true);
            }}
            className="pdf-cover-btn"
            title="العودة للغلاف"
            disabled={isFlipping}
          >
            📚 الغلاف
          </button>
        </div>

        <div className="pdf-zoom-controls">
          <button onClick={zoomOut} className="pdf-zoom-btn" title="تصغير">
            🔍-
          </button>
          <button onClick={resetZoom} className="pdf-zoom-btn pdf-reset-zoom" title="إعادة تعيين">
            🔍
          </button>
          <button onClick={zoomIn} className="pdf-zoom-btn" title="تكبير">
            🔍+
          </button>
        </div>

        <div className="pdf-position-controls">
          <button 
            onClick={() => setBookPosition({ x: 0, y: 0 })}
            className="pdf-position-btn"
            title="إعادة تعيين الموضع"
            disabled={scale <= 1.0}
          >
            🎯
          </button>
        </div>

        <div className="pdf-page-jump">
          <input
            type="number"
            min="0"
            max={totalPages}
            value={currentPage}
            onChange={(e) => goToPage(e.target.value)}
            className="pdf-page-input"
            placeholder="رقم الصفحة"
            disabled={isFlipping}
          />
          <button onClick={() => goToPage(currentPage)} className="pdf-jump-btn" disabled={isFlipping}>
            انتقل
          </button>
        </div>
      </div>

      {/* Page Thumbnails */}
      <div className="pdf-thumbnails">
        <h4>معاينة الصفحات</h4>
        <div className="pdf-thumbnails-grid">
          {Array.from({ length: totalPages + 1 }, (_, index) => (
            <div
              key={`page_${index}`}
              className={`pdf-thumbnail ${currentPage === index ? 'active' : ''}`}
              onClick={() => goToPage(index)}
            >
              <div className="pdf-thumbnail-placeholder">
                <span className="pdf-page-number">
                  {index === 0 ? 'غلاف' : index}
                </span>
              </div>
              <span className="pdf-thumbnail-number">
                {index === 0 ? 'غلاف' : index}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
